/**
 * Pending Users Page
 *
 * Lists users awaiting approval with ability to approve/reject
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  getPendingUsers,
  getAllOrganizations,
  approveUserWithSeatValidation,
  rejectUser,
  checkSeatAvailability,
  getSeatTypeForRole,
} from '../../../services/firestore';
import type { UserProfile, Organization, UserRole } from '../../../services/firestore';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import './PendingUsers.css';

interface ApprovalModalState {
  isOpen: boolean;
  user: UserProfile | null;
}

const ROLE_OPTIONS: { value: UserRole; label: string; orgTypes: string[] }[] = [
  { value: 'platform_admin', label: 'Platform Admin', orgTypes: ['platform'] },
  { value: 'cra_admin', label: 'CRA Admin', orgTypes: ['cra'] },
  { value: 'cra_analyst', label: 'CRA Analyst', orgTypes: ['cra'] },
  { value: 'rating_committee', label: 'Rating Committee', orgTypes: ['cra'] },
  { value: 'bank_admin', label: 'Bank Admin', orgTypes: ['bank'] },
  { value: 'bank_user', label: 'Bank User', orgTypes: ['bank'] },
];

export const PendingUsers: React.FC = () => {
  const { userProfile, isPlatformAdmin } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<UserProfile[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ApprovalModalState>({ isOpen: false, user: null });
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('');
  const [processing, setProcessing] = useState(false);
  const [seatInfo, setSeatInfo] = useState<{ available: boolean; used: number; total: number; seatType: 'analyst' | 'bank' | null } | null>(null);
  const [checkingSeats, setCheckingSeats] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Only platform admins can see pending users (they have no org yet)
      // CRA/Bank admins cannot approve users who haven't been assigned to their org
      if (!isPlatformAdmin) {
        setPendingUsers([]);
        setOrganizations([]);
        setLoading(false);
        return;
      }

      const [users, orgs] = await Promise.all([
        getPendingUsers(),
        getAllOrganizations(),
      ]);

      setPendingUsers(users);
      setOrganizations(orgs);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setError('Failed to load pending users');
    } finally {
      setLoading(false);
    }
  };

  const openApprovalModal = (user: UserProfile) => {
    setModal({ isOpen: true, user });
    setSelectedOrg('');
    setSelectedRole('');
  };

  const closeModal = () => {
    setModal({ isOpen: false, user: null });
    setSelectedOrg('');
    setSelectedRole('');
    setSeatInfo(null);
  };

  // Check seat availability when org and role are selected
  const handleRoleChange = async (role: UserRole) => {
    setSelectedRole(role);
    setSeatInfo(null);

    if (!selectedOrg || !role) return;

    const seatType = getSeatTypeForRole(role);
    if (!seatType) {
      // Admin roles don't consume seats
      setSeatInfo({ available: true, used: 0, total: 0, seatType: null });
      return;
    }

    try {
      setCheckingSeats(true);
      const availability = await checkSeatAvailability(selectedOrg, role);
      setSeatInfo(availability);
    } catch (err) {
      console.error('Error checking seat availability:', err);
    } finally {
      setCheckingSeats(false);
    }
  };

  const handleApprove = async () => {
    if (!modal.user || !selectedOrg || !selectedRole) return;

    try {
      setProcessing(true);
      setError(null);

      const result = await approveUserWithSeatValidation(
        modal.user.uid,
        selectedOrg,
        selectedRole,
        userProfile?.uid || 'unknown'
      );

      if (!result.success) {
        setError(result.error || 'Failed to approve user');
        return;
      }

      // Refresh the list
      await fetchData();
      closeModal();
    } catch (err) {
      console.error('Error approving user:', err);
      setError('Failed to approve user');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (user: UserProfile) => {
    if (!confirm(`Are you sure you want to reject ${user.email}?`)) return;

    try {
      setProcessing(true);
      await rejectUser(user.uid, userProfile?.uid || 'unknown');
      await fetchData();
    } catch (err) {
      console.error('Error rejecting user:', err);
      setError('Failed to reject user');
    } finally {
      setProcessing(false);
    }
  };

  // Get available roles based on selected organization type
  const getAvailableRoles = () => {
    if (!selectedOrg) return [];
    const org = organizations.find(o => o.id === selectedOrg);
    if (!org) return [];
    return ROLE_OPTIONS.filter(role => role.orgTypes.includes(org.type));
  };

  if (loading) {
    return (
      <div className="pending-users-page">
        <h1>Pending Approvals</h1>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pending-users-page">
      <h1>Pending Approvals</h1>

      {error && <div className="error-message">{error}</div>}

      {!isPlatformAdmin ? (
        <div className="empty-state">
          <span className="empty-icon">🔒</span>
          <h3>Platform Admin Access Required</h3>
          <p>Only Platform Admins can approve new user registrations.</p>
        </div>
      ) : pendingUsers.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">✅</span>
          <h3>No Pending Approvals</h3>
          <p>All users have been reviewed.</p>
        </div>
      ) : (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Auth Provider</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingUsers.map(user => (
                <tr key={user.uid}>
                  <td className="email-cell">{user.email}</td>
                  <td>{user.display_name || '-'}</td>
                  <td>
                    <span className={`provider-badge ${user.auth_provider}`}>
                      {user.auth_provider}
                    </span>
                  </td>
                  <td>
                    {user.created_at?.toDate
                      ? user.created_at.toDate().toLocaleDateString()
                      : 'Unknown'}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="approve-btn"
                      onClick={() => openApprovalModal(user)}
                      disabled={processing}
                    >
                      Approve
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleReject(user)}
                      disabled={processing}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approval Modal */}
      {modal.isOpen && modal.user && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Approve User</h2>
            <p className="modal-email">{modal.user.email}</p>

            <div className="form-group">
              <label htmlFor="organization">Organization</label>
              <select
                id="organization"
                value={selectedOrg}
                onChange={e => {
                  setSelectedOrg(e.target.value);
                  setSelectedRole('');
                }}
              >
                <option value="">Select organization...</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.type.toUpperCase()})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={selectedRole}
                onChange={e => handleRoleChange(e.target.value as UserRole)}
                disabled={!selectedOrg}
              >
                <option value="">Select role...</option>
                {getAvailableRoles().map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Seat availability indicator */}
            {checkingSeats && (
              <div className="seat-info checking">
                Checking seat availability...
              </div>
            )}
            {seatInfo && !checkingSeats && (
              <div className={`seat-info ${seatInfo.available ? 'available' : 'unavailable'}`}>
                {seatInfo.seatType === null ? (
                  <span className="no-seats-required">Admin roles do not consume seats</span>
                ) : seatInfo.available ? (
                  <span className="seats-available">
                    ✓ Seats available: {seatInfo.used}/{seatInfo.total} {seatInfo.seatType} seats used
                  </span>
                ) : (
                  <span className="seats-full">
                    ✗ No seats available: {seatInfo.used}/{seatInfo.total} {seatInfo.seatType} seats used
                  </span>
                )}
              </div>
            )}

            {error && <div className="modal-error">{error}</div>}

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeModal} disabled={processing}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleApprove}
                disabled={!selectedOrg || !selectedRole || processing || checkingSeats || (seatInfo !== null && !seatInfo.available)}
              >
                {processing ? 'Processing...' : 'Approve User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingUsers;
